import React, { useEffect, useRef, useState } from "react";
import { ArrowClockwise, Clipboard, Printer } from "react-bootstrap-icons";
import {
  buildClipboardPayload,
  writeClipboard,
} from "../EOYReportAssistant/ReportCard";
import { printReportNodes } from "../EOYReportAssistant/printReports";
import PrintHeader from "../PrintHeader";
import { reportHasContent } from "./useEnhancedReportWorkflow";
import "../PrintStudentResults.css";
import "../EOYReportAssistant/EOYReportAssistant.css";

const SECTION_LABELS = {
  doing_well: "What your child is doing well",
  learning_next: "What your child is learning next",
  help_at_home: "Simple ways to help at home",
};
const SECTION_ORDER = ["doing_well", "learning_next", "help_at_home"];

function clearTimer(timerRef) {
  if (!timerRef.current) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

/**
 * Parent Teacher Meeting report card — read only. Unlike New Class Insights
 * and Screening Point, the PTM response has no `scores` field (the backend
 * doesn't expose raw screener data for this report type), so there's no
 * ScoresTable here. `report.report` and `report.report_sections` carry the
 * same content in two shapes (one unbroken block vs. parsed per heading) —
 * only the parsed sections render when available, so the three parent-facing
 * headings show once each rather than twice.
 */
function PTMReportCard({
  report,
  className,
  academicYear,
  onReportNodeReady,
  onRegenerateReport,
  regenerating,
}) {
  const [copyStatus, setCopyStatus] = useState("");
  const reportShellRef = useRef(null);
  const activePrintCleanupRef = useRef(null);
  const copyStatusTimer = useRef(null);

  const studentId = report?.student_id;

  useEffect(() => {
    return () => {
      clearTimer(copyStatusTimer);
      activePrintCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!report?.success || !studentId || !onReportNodeReady) return undefined;
    onReportNodeReady(studentId, reportShellRef.current);
    return () => onReportNodeReady(studentId, null);
  }, [onReportNodeReady, studentId, report?.success]);

  if (!report) return null;

  const displayId = report.display_name || report.student_id;
  const isRegenerationAvailable = report.can_regenerate === true;

  if (!report.success) {
    return (
      <div className="eoy-report-card eoy-report-card-error">
        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          <span className="eoy-report-badge eoy-report-badge-error">
            {isRegenerationAvailable ? "Update available" : "Failed"}
          </span>
        </div>
        <p className="eoy-report-text">
          {report.error?.message || "Report could not be generated."}
        </p>
        {onRegenerateReport && studentId && (
          <button
            aria-label={`Regenerate report for ${displayId}`}
            className="eoy-secondary-btn eoy-regenerate-report-btn"
            data-tooltip={`Regenerate report for ${displayId}`}
            disabled={regenerating}
            onClick={() => onRegenerateReport(report)}
            type="button"
          >
            <ArrowClockwise aria-hidden="true" size={15} />
            {regenerating ? "Regenerating..." : "Regenerate report"}
          </button>
        )}
      </div>
    );
  }

  // A successful response can still carry no usable content — either an
  // explicit placeholder (no completed screeners yet) or an empty cached
  // record — and needs to say so rather than rendering an empty card body.
  if (report.placeholder || !reportHasContent(report)) {
    return (
      <div className="eoy-report-card eoy-report-card-placeholder">
        <div className="eoy-report-header">
          <strong>{displayId}</strong>
        </div>
        <p className="eoy-report-text eoy-muted">
          {report.report ||
            `No completed screening information is available for ${displayId}, so a Parent Teacher Meeting handout cannot be prepared yet.`}
        </p>
        {onRegenerateReport && studentId && !report.placeholder && (
          <button
            aria-label={`Regenerate report for ${displayId}`}
            className="eoy-secondary-btn eoy-regenerate-report-btn"
            data-tooltip={`Regenerate report for ${displayId}`}
            disabled={regenerating}
            onClick={() => onRegenerateReport(report)}
            type="button"
          >
            <ArrowClockwise aria-hidden="true" size={15} />
            {regenerating ? "Regenerating..." : "Regenerate report"}
          </button>
        )}
      </div>
    );
  }

  const sections = report.report_sections || {};
  const hasParsedSections = SECTION_ORDER.some((key) => sections[key]);
  const screeningPointsUsed = report.screening_points_used || [];

  const handlePrintReport = () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;
    activePrintCleanupRef.current?.();
    activePrintCleanupRef.current = printReportNodes([reportNode]);
  };

  const handleCopyReport = async () => {
    const reportNode = reportShellRef.current;
    if (!reportNode) return;

    try {
      const copied = await writeClipboard(buildClipboardPayload(reportNode));
      setCopyStatus(copied ? "Copied" : "Copy failed");
    } catch (err) {
      console.warn("Could not copy PTM report:", err);
      setCopyStatus("Copy failed");
    }
    clearTimer(copyStatusTimer);
    copyStatusTimer.current = window.setTimeout(() => {
      setCopyStatus("");
      copyStatusTimer.current = null;
    }, 1800);
  };

  return (
    <div className="eoy-report-print-shell" ref={reportShellRef}>
      <div className="eoy-report-card">
        <PrintHeader
          className={className}
          academicYear={report.academic_year || academicYear}
          assessmentName="Parent Teacher Meeting"
        />
        <div className="eoy-report-toolbar no-print">
          <span
            className={`eoy-copy-status ${copyStatus ? "" : "eoy-copy-status-hidden"}`}
            aria-live="polite"
          >
            {copyStatus || "Copied"}
          </span>
          <button
            aria-label="Copy report to clipboard"
            className="eoy-toolbar-icon-btn"
            onClick={handleCopyReport}
            data-tooltip="Copy report to clipboard"
            type="button"
          >
            <Clipboard aria-hidden="true" size={16} />
          </button>
          <button
            aria-label="Print this report"
            className="eoy-toolbar-icon-btn"
            onClick={handlePrintReport}
            data-tooltip="Print this report"
            type="button"
          >
            <Printer aria-hidden="true" size={16} />
          </button>
        </div>

        <div className="eoy-report-header">
          <strong>{displayId}</strong>
          {screeningPointsUsed.length > 0 && (
            <small>Based on: {screeningPointsUsed.join(", ")}</small>
          )}
        </div>

        {/* report.report is the same content as report_sections, just as one
            unbroken block of text with the section headings baked in — only
            shown as a fallback for older cached reports saved before
            report_sections existed, to avoid showing every section twice. */}
        {!hasParsedSections && report.report && (
          <div className="eoy-parent-report-section">
            <p className="eoy-report-text no-print">{report.report}</p>
            <div className="eoy-report-print-summary" aria-hidden="true">
              {report.report}
            </div>
          </div>
        )}

        {hasParsedSections &&
          SECTION_ORDER.filter((key) => sections[key]).map((key) => (
            <div className="eoy-scores-table-wrap" key={key}>
              <div className="eoy-scores-table-header">{SECTION_LABELS[key]}</div>
              <p className="eoy-report-text">{sections[key]}</p>
            </div>
          ))}
      </div>
    </div>
  );
}

export default PTMReportCard;
